import { Component } from "@kluntje/core";
import { fetchJSON } from "Helper/apiHelper";
import { addClass, removeClass, removeEvent } from "@kluntje/js-utils/lib/dom-helpers";
import { ACTIVE } from "Helper/cssClasses";
import { render, html } from "lit-html";
import { Placeholder } from "Interfaces/flightsInformation";
import { getDocLocale } from "Helper/metaHelper";
import { I18nService } from "Services/I18nService";
import { ViewportObserver } from "@kluntje/services";
import {
  convertTime,
  convertOverviewFlightState,
  convertMinutes,
  validateBaggageState,
  renderStringArray,
} from "Helper/flightInformationHelper";

import {
  flightsDetailTemplate,
  updatesOverlayTemplate,
  airlineContactInformation,
  isTrain,
} from "./fra-m-flight-info__list.template"; // eslint-disable-line

/**
 * Custom Element to render new flight info
 */
class FRAFlightInfo extends Component {
  viewportObserver = ViewportObserver.getInstance();
  updateInterval!: NodeJS.Timeout;

  constructor() {
    super({
      ui: {
        detailList: ".fra-js-flight-info__facts:-one",
        processListLabels: ".fra-m-flight-process__steps-label .fra-m-flight-info__fact-data span[data-flight-info]",
        processListItems: ".fra-m-flight-process__steps-label .fra-h6",
        flightAwareCTA: ".fra-js-toggle-flight-aware:-one",
        flightAwareDisplay: ".fra-m-flight-info__flight-aware:-one",
        flightWeather: ".fra-m-flight-process__steps-item.fra-m-flight--weather",
        flightWeatherIcon: ".fra-m-flight-info__weather-icon",
        flightWeatherTemperatureMin: ".fra-m-flight-info__weather .fra-h2 .fra-m-weather-min",
        flightWeatherTemperatureMax: ".fra-m-flight-info__weather .fra-h2 .fra-m-weather-max",
        flightUpdates: ".fra-e-button.fra-m-flight-info__more-info-updates :-one",
        flightUpdatesOverlay: ".fra-js-flight-info__flight-updates-overlay :-one",
        whatsappUpdatesLink: ".fra-js-flight-info__flight-updates-overlay .fra-m-flightupdate__whatsapp-link :-one",
        whatsappAlert: ".fra-js-flight-info__flight-updates-overlay .fra-m-flightupdate__whatsapp-alert :-one",
        flightUpdatesOverlayContent: ".fra-m-flight-info__flight-updates-content :-one",
        trainUpdatesOverlay: ".fra-js-flight-info__train-updates-overlay :-one",
        trainOverlayContent: ".fra-js-flight-info__train-updates-overlay .fra-m-flight-info__overlay-content :-one",
        flightLiveTracker: ".fra-m-flight-info__more-info-livetrack :-one",
        flightLiveTrackerOverlay: ".fra-js-flight-info__flight-live-tracker-overlay :-one",
        flightLiveTrackerOverlayContent: ".fra-m-flight-info__flight-live-tracker-content :-one",
      },
      initialStates: {
        flightDetails: [],
      },
      events: [
        {
          event: "click",
          target: "flightUpdates",
          handler: "openUpdatesOverlay",
        },
        {
          event: "kl-in-vp",
          target: "processListItems",
          handler: "handleIntersectionChange",
        },
      ],
      reactions: {
        flightDetails: ["handleDataUpdate"],
      },
    });
  }

  /**
   * Handles Intersection Change, by removing viewportObserver
   * and initializing flight process section loaded
   * @param {Event} e
   */
  handleIntersectionChange(e: Event): void {
    const currentElement = e.currentTarget as HTMLElement;
    removeEvent(currentElement, "kl-in-vp", this.handleIntersectionChange, this);
    const data = JSON.parse(currentElement.getAttribute("data-gatracking") as any);
    if (data) window.dataLayer?.push(data);
  }

  /**
   * Returns siteroot path
   * @returns {string}
   */
  get siteRootPath(): string {
    return this.getAttribute("data-flight-siteroot-path") || "";
  }

  /**
   * Returns provided api-url from custom element
   * @returns {string}
   */
  get apiUrl(): string {
    return this.getAttribute("data-api-url") || "";
  }

  /**
   * Returns provided airline-url from custom element
   * @returns {string}
   */
  get airlineUrl(): string {
    return this.getAttribute("data-airline-url") || "";
  }

  /**
   * Returns provided airline-url from custom element
   * @returns {string}
   */
  get airlineContactInfo(): airlineContactInformation {
    return {
      phone: this.getAttribute("data-airline-contact-phone") || "",
      website: this.getAttribute("data-airline-contact-website") || "",
      websiteTitle: this.getAttribute("data-airline-contact-websiteTitle") || "",
    };
  }

  /**
   * Returns provided airline-delay-url from custom element
   * @returns {string}
   */
  get airlineDelayUrl(): string {
    return this.getAttribute("data-airline-delay-url") || "";
  }

  /**
   * Returns provided flight-aware iframe url
   */
  get flightAwareUrl(): string {
    return this.getAttribute("data-flight-aware-url") || "";
  }

  /**
   * Returns provided flight weather REST url
   */
  get flightWeatherApiUrl(): string {
    return this.getAttribute("data-weather-api-url") || "";
  }

  /**
   * Returns provided locale from custom element
   * @returns {string}
   */
  get locale(): string {
    return getDocLocale();
  }

  /**
   * Lifecycle-Hook
   * @override
   */
  afterComponentRender(): void {
    this.fetchFlightDetails();
    this.fetchWeatherDetails();
    this.updateComponent();
    this.ui.processListItems.forEach((element: Element) => this.viewportObserver.observe(element));
  }

  /**
   * Update Component every 2 Minutes
   */
  updateComponent(): void {
    this.updateInterval = setInterval(this.fetchFlightDetails.bind(this), 120000);
  }

  removeWeather(): void {
    if (this.ui.flightWeather !== null) {
      this.ui.flightWeather.forEach(function (elem: { remove: () => void }) {
        elem.remove();
      });
    }
  }

  /**
   * Fetch data from API and store response in state
   */
  async fetchFlightDetails(): Promise<void> {
    const apiResponse = await fetchJSON(this.apiUrl);
    this.setState({ flightDetails: apiResponse });
  }

  /**
   * Fetch data from API and store response in state
   */
  async fetchWeatherDetails(): Promise<void> {
    if (this.flightWeatherApiUrl !== "") {
      const apiResponse = await fetchJSON(this.flightWeatherApiUrl);
      if (apiResponse.error) {
        this.removeWeather();
      } else {
        const icon = apiResponse.icon;
        const temperatureMin = apiResponse.min;
        const temperatureMax = apiResponse.max;

        if (temperatureMin === undefined || temperatureMax === undefined) {
          this.removeWeather();
        } else {
          if (this.ui.flightWeatherIcon) {
            this.ui.flightWeatherIcon.forEach(function (elem: HTMLElement) {
              elem.setAttribute("icon-id", icon);
            });
          }
          if (this.ui.flightWeatherTemperatureMin) {
            this.ui.flightWeatherTemperatureMin.forEach(function (elem: HTMLElement) {
              elem.innerHTML = `${temperatureMin}°C`;
            });
          }
          if (this.ui.flightWeatherTemperatureMax) {
            this.ui.flightWeatherTemperatureMax.forEach(function (elem: HTMLElement) {
              elem.innerHTML = `${temperatureMax}°C`;
            });
          }
        }
      }
    } else if (this.ui.flightWeather !== null) {
      // hide weather process item
      this.removeWeather();
    }
  }

  /**
   * Reaction on state change
   */
  async handleDataUpdate(): Promise<void> {
    await this.renderList();
    this.updateUI();
    this.updateEvents();

    this.renderProcess();
  }

  /**
   * Handle click on FlightUpdates button
   */
  openUpdatesOverlay() {
    const content = updatesOverlayTemplate(this.state.flightDetails);
    if (isTrain(this.state.flightDetails?.ac)) {
      render(content, this.ui.trainOverlayContent);
      this.ui.trainUpdatesOverlay?.open();
    } else {
      this.checkAlertTime();
      render(content, this.ui.flightUpdatesOverlayContent);
      this.ui.flightUpdatesOverlay?.open();
    }
  }

  /**
   * Function to show a WhatsApp alert if the flight is 47 hours or more before the current time.
   */
  private checkAlertTime(): void {
    const flightType = this.state.flightDetails.id.startsWith("d") ? "departure" : "arrival";

    const targetDate = this.state.flightDetails.sched;
    if (!targetDate) return;

    // Calculate the time difference between the current time and the target date in milliseconds.
    const timeDifference = new Date(targetDate).getTime() - new Date().getTime();
    const is46HrsBefore = timeDifference / (1000 * 60 * 60) >= 46;

    if (is46HrsBefore) {
      removeClass(this.ui.whatsappUpdatesLink, ACTIVE);
      addClass(this.ui.whatsappAlert, ACTIVE);
      const content = html`
        ${I18nService.getInstance().getKey(
          `fraport.travel.components.content.flight.overlay.${flightType}.alert.label`,
        )}
      `;
      render(content, this.ui.whatsappAlert);
    }
  }

  /**
   * Rendering facts at top with lit
   */
  async renderList(): Promise<void> {
    await I18nService.getInstance().waitForJson();
    const input = flightsDetailTemplate(
      this.state.flightDetails,
      this.airlineUrl,
      this.airlineDelayUrl,
      this.flightAwareUrl,
      this.locale,
      this.siteRootPath,
    );
    render(input, this.ui.detailList);
  }

  /**
   * Render general flight info in accordion
   * Replaces placeholder w/ data from flight
   */
  renderProcess(): void {
    // prepare duration as hh:mm format if avail
    let duration = "";
    if (this.state.flightDetails.duration) {
      const durationValues = convertMinutes(this.state.flightDetails.duration);
      duration = `${durationValues[0]}:${durationValues[1]}`;
    }

    // prepare values from JSON
    const placeholderData: Placeholder = {
      boarding: convertTime(this.state.flightDetails.sched) || "",
      departure: convertTime(this.state.flightDetails.schedDep) || "",
      arrival: convertTime(this.state.flightDetails.schedArr) || "",
      gate: this.state.flightDetails.gate || "",
      reg: this.state.flightDetails.reg || "",
      terminal: this.state.flightDetails.terminal || "",
      halle: this.state.flightDetails.halle || "",
      schalter: this.state.flightDetails.schalter || "",
      ausgang: this.state.flightDetails.ausgang || "-",
      status: this.state.flightDetails.status || "-",
      baggage: this.state.flightDetails.baggage || "-",
      bagclaim: renderStringArray(this.state.flightDetails.bag),
      airline: this.state.flightDetails.alname || "",
      flight: this.state.flightDetails.fnr || "",
      codeshare: renderStringArray(this.state.flightDetails.cs),
      duration,
      destination: this.state.flightDetails.apname || "",
      airplane: this.state.flightDetails.ac || "",
      typ: this.state.flightDetails.typ || "",
      stops: this.state.flightDetails.stops || "",
      estimated: this.state.flightDetails.esti || "",
      pos: this.state.flightDetails.pos || "",
      location: this.state.flightDetails.id.startsWith("d") ? this.state.flightDetails.apname : "Frankfurt",
    };

    // replace all instances of placeholder
    for (const entry of this.ui.processListLabels) {
      // in case it is status we need to render HTML
      if (entry.dataset.flightInfo === "status" && this.state.flightDetails.status) {
        entry.innerHTML = this.renderStatus(this.state.flightDetails?.flstatus, this.state.flightDetails?.status);
      } else if (entry.dataset.flightInfo === "baggage" && validateBaggageState(this.state.flightDetails.status)) {
        entry.innerHTML = `<span class="fra-m-flight-info__flight-status-${convertOverviewFlightState(
          this.state.flightDetails.flstatus,
          this.state.flightDetails.status,
        )}">${placeholderData.status}</span>`;
      } else if (entry.dataset.flightInfo === "schalter" && this.state.flightDetails.ac === "TRS") {
        let value = placeholderData[entry.dataset.flightInfo as keyof Placeholder];
        value = value !== undefined && value !== "" ? value.concat(", <br/>AirRail") : "<br/>AirRail";
        entry.innerHTML = value;
      } else if ((entry.dataset.flightInfo === "gate" || entry.dataset.flightInfo === "pos") && this.state.flightDetails.ac === "TRS") {
        let value = placeholderData[entry.dataset.flightInfo as keyof Placeholder];
        // removes leading T in front of train track
        value = value.length >= 2 ? value.substring(1) : value;
        entry.innerHTML = value;
      } else {
        entry.innerText = placeholderData[entry.dataset.flightInfo as keyof Placeholder];
      }
    }
  }

  renderStatus(flstatus: number, status: string) {
    if (status) {
      return `
        <span class="fra-e-list__desc fra-m-flight-info__flight-status-${convertOverviewFlightState(flstatus, status)}">
          ${
            validateBaggageState(status)
              ? `<span class="fra-m-flight-info__flight-status">
                  <fra-e-icon class="fra-e-icon" icon-id="fra-icon-gepaeck-klein"></fra-e-icon>
                  <span class="fra-m-flight-info__flight-status-content">${status}</span>
                </span>`
              : `<span class="fra-m-flight-info__flight-status">
                  <span class="fra-m-flight-info__flight-status-indicator"></span>
                  <span class="fra-m-flight-info__flight-status-content">${status}</span>
                </span>`
          }
        </span>
      `;
    }
    return ``;
  }
}

customElements.define("fra-m-flight-info", FRAFlightInfo);

export default FRAFlightInfo;
